import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import {
  Globe,
  Save,
  Upload,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Camera,
  ImageIcon,
  ChevronDown,
  ChevronRight,
  Pencil,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { resizeImage } from "@/lib/imageUtils";
import Cropper, { type Area } from "react-easy-crop";
import PartnerLayout from "./PartnerLayout";

interface TextEntry { name: string; locale: string; text: string; template?: string; version?: string; source?: 'uploaded' | 'default'; }
interface MediaEntry { name: string; file?: string; url?: string; upload_id?: string; width?: number; height?: number; is_video?: boolean; template?: string; version?: string; source?: 'uploaded' | 'default'; }
interface WebsiteData {
  id?: number;
  status?: string;
  live_website?: string;
  text: TextEntry[];
  media: MediaEntry[];
  profile_uid?: string;
  cdn_domain?: string;
}

const IMAGE_SPECS: Record<string, { width: number; height: number; aspectRatio: number; label: string }> = {
  first_slide: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Hero Slide 1" },
  second_slide: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Hero Slide 2" },
  third_slide: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Hero Slide 3" },
  about_bg: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "About Background" },
  contact_hours: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Contact Section" },
  custom_image_01: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Custom Section 1" },
  custom_image_02: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Custom Section 2" },
  custom_image_03: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Custom Section 3" },
  custom_image_04: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Custom Section 4" },
  menu_sample: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Featured Dish" },
  site_logo: { width: 630, height: 330, aspectRatio: 630 / 330, label: "Site Logo" },
  consumer_restaurant_page_logo: { width: 350, height: 140, aspectRatio: 2.5, label: "Restaurant Page Logo" },
  consumer_restaurant_checkout_logo: { width: 256, height: 100, aspectRatio: 2.56, label: "Checkout Logo" },
  consumer_restaurant_order_success_logo: { width: 256, height: 100, aspectRatio: 2.56, label: "Order Success Logo" },
  consumer_restaurant_stripe_logo: { width: 256, height: 100, aspectRatio: 2.56, label: "Stripe Logo" },
  consumer_restaurant_email_logo: { width: 512, height: 512, aspectRatio: 1, label: "Email Logo" },
  consumer_restaurant_list_logo: { width: 512, height: 320, aspectRatio: 16 / 10, label: "App Listing Logo" },
  consumer_restaurant_map_logo: { width: 512, height: 320, aspectRatio: 16 / 10, label: "Map Pin Logo" },
  consumer_restaurant_page_background: { width: 1024, height: 640, aspectRatio: 16 / 10, label: "Page Background" },
  consumer_restaurant_history_item_logo: { width: 96, height: 96, aspectRatio: 1, label: "Order History Logo" },
  consumer_restaurant_tracking_details_logo: { width: 96, height: 96, aspectRatio: 1, label: "Tracking Details Logo" },
  consumer_restaurant_tracking_item_logo: { width: 96, height: 96, aspectRatio: 1, label: "Tracking Item Logo" },
  favicon: { width: 96, height: 96, aspectRatio: 1, label: "Favicon" },
};

function getTextValue(textArray: TextEntry[], name: string): string {
  return textArray?.find((t) => t.name === name && t.locale === "en")?.text || "";
}

function setTextValue(textArray: TextEntry[], name: string, value: string): TextEntry[] {
  const existing = textArray.findIndex((t) => t.name === name && t.locale === "en");
  const newArray = [...textArray];
  if (existing >= 0) {
    newArray[existing] = { ...newArray[existing], text: value };
  } else {
    newArray.push({ name, locale: "en", text: value });
  }
  return newArray;
}

function getMediaUrl(mediaArray: MediaEntry[], name: string, width = 400, height = 400): string {
  const entry = mediaArray?.find((m) => m.name === name);
  if (!entry) return "";
  let url = entry.url || "";
  if (!url && entry.file) {
    url = `//${entry.file}`;
  }
  if (!url) return "";
  if (!url.startsWith("http")) {
    url = url.startsWith("//") ? `https:${url}` : `https://${url}`;
  }
  return `${url}?width=${width}&height=${height}&_v=${Date.now()}`;
}

function extractFileFromCdnUrl(cdnUrl: string): string {
  let url = cdnUrl;
  if (url.startsWith("//")) url = url.substring(2);
  else if (url.startsWith("https://")) url = url.substring(8);
  else if (url.startsWith("http://")) url = url.substring(7);
  const slashIdx = url.indexOf("/");
  if (slashIdx >= 0) {
    return url.substring(slashIdx + 1);
  }
  return url;
}

function updateMediaEntry(mediaArray: MediaEntry[], name: string, file: string, url: string, width?: number, height?: number): MediaEntry[] {
  const newArray = [...mediaArray];
  const idx = newArray.findIndex((m) => m.name === name);
  const entry: MediaEntry = { name, file, url, width, height };
  if (idx >= 0) {
    newArray[idx] = { ...newArray[idx], ...entry };
  } else {
    newArray.push(entry);
  }
  return newArray;
}

function getMenuSampleEntries(mediaArray: MediaEntry[]): MediaEntry[] {
  return mediaArray.filter(m => m.name === "menu_sample");
}

function getMenuSampleUrl(mediaArray: MediaEntry[], index: number, width = 400, height = 400): string {
  const entries = getMenuSampleEntries(mediaArray);
  const entry = entries[index];
  if (!entry) return "";
  let url = entry.url || "";
  if (!url && entry.file) {
    url = `//${entry.file}`;
  }
  if (!url) return "";
  if (!url.startsWith("http")) {
    url = url.startsWith("//") ? `https:${url}` : `https://${url}`;
  }
  return `${url}?width=${width}&height=${height}&_v=${Date.now()}`;
}

function updateMenuSampleMedia(mediaArray: MediaEntry[], index: number, file: string, url: string): MediaEntry[] {
  const newArray = [...mediaArray];
  const menuSamples = newArray.filter(m => m.name === "menu_sample");
  const otherMedia = newArray.filter(m => m.name !== "menu_sample");
  while (menuSamples.length <= index) {
    menuSamples.push({ name: "menu_sample", file: "", url: "" });
  }
  menuSamples[index] = { name: "menu_sample", file, url };
  return [...otherMedia, ...menuSamples];
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error('Failed to load image'));
  });
  if (isNaN(pixelCrop.x) || isNaN(pixelCrop.y) || isNaN(pixelCrop.width) || isNaN(pixelCrop.height)) {
    throw new Error('Invalid crop area');
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  canvas.width = Math.round(pixelCrop.width);
  canvas.height = Math.round(pixelCrop.height);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const sx = Math.max(0, Math.round(pixelCrop.x));
  const sy = Math.max(0, Math.round(pixelCrop.y));
  const sRight = Math.min(image.naturalWidth, Math.round(pixelCrop.x + pixelCrop.width));
  const sBottom = Math.min(image.naturalHeight, Math.round(pixelCrop.y + pixelCrop.height));
  const sw = sRight - sx;
  const sh = sBottom - sy;
  const dx = sx - Math.round(pixelCrop.x);
  const dy = sy - Math.round(pixelCrop.y);
  if (sw > 0 && sh > 0) {
    ctx.drawImage(image, sx, sy, sw, sh, dx, dy, sw, sh);
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => { if (blob) resolve(blob); else reject(new Error('Failed to create blob')); }, 'image/jpeg', 0.9);
  });
}

function getApiHeaders() {
  return {
    "X-Eatzy-Token": sessionStorage.getItem("partnerEatzyToken") || "",
    "x-partner-user-id": localStorage.getItem("partnerUserId") || "",
    "x-restaurant-id": localStorage.getItem("partnerRestaurantId") || "",
    Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
  };
}

function ImageCropDialog({ open, onClose, imageSrc, mediaName, onCropComplete }: {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  mediaName: string;
  onCropComplete: (blob: Blob) => void;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [applying, setApplying] = useState(false);
  const { toast } = useToast();

  const specKey = mediaName.startsWith("menu_sample_") ? "menu_sample" : mediaName;
  const spec = IMAGE_SPECS[specKey] || { width: 1024, height: 640, aspectRatio: 16 / 10, label: mediaName };

  const handleApply = async () => {
    console.log("[WebsiteUpload] CropDialog handleApply called, croppedAreaPixels:", croppedAreaPixels, "mediaName:", mediaName);
    if (!croppedAreaPixels) {
      console.error("[WebsiteUpload] CropDialog: croppedAreaPixels is null, Apply button should be disabled");
      return;
    }
    setApplying(true);
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      console.log("[WebsiteUpload] CropDialog: cropped blob size:", cropped.size);
      const resized = await resizeImage(cropped, { maxWidth: spec.width, maxHeight: spec.height, quality: 0.9 });
      console.log("[WebsiteUpload] CropDialog: resized blob size:", resized.blob.size, "calling onCropComplete");
      onCropComplete(resized.blob);
      onClose();
    } catch (err: any) {
      console.error("[WebsiteUpload] CropDialog: crop/resize failed:", err);
      toast({ title: "Crop failed", description: err.message, variant: "destructive" });
    } finally {
      setApplying(false);
    }
  };

  const handleUseWithoutCrop = async () => {
    console.log("[WebsiteUpload] CropDialog: Using image without cropping for:", mediaName);
    setApplying(true);
    try {
      const response = await fetch(imageSrc);
      const originalBlob = await response.blob();
      const resized = await resizeImage(originalBlob, { maxWidth: spec.width, maxHeight: spec.height, quality: 0.9 });
      console.log("[WebsiteUpload] CropDialog: resized (no crop) blob size:", resized.blob.size);
      onCropComplete(resized.blob);
      onClose();
    } catch (err: any) {
      console.error("[WebsiteUpload] CropDialog: use-without-crop failed:", err);
      toast({ title: "Processing failed", description: err.message, variant: "destructive" });
    } finally {
      setApplying(false);
    }
  };

  const onMediaLoaded = useCallback((mediaSize: { naturalWidth: number; naturalHeight: number }) => {
    const imageAspect = mediaSize.naturalWidth / mediaSize.naturalHeight;
    const cropAspect = spec.aspectRatio;
    let computed = 1;
    if (imageAspect > cropAspect) {
      computed = cropAspect / imageAspect;
    } else if (imageAspect < cropAspect) {
      computed = imageAspect / cropAspect;
    }
    const safeMin = Math.max(0.1, Math.round(computed * 100) / 100);
    setMinZoom(safeMin);
    setZoom(safeMin);
    setCrop({ x: 0, y: 0 });
    setMediaLoaded(true);
    setImageLoadFailed(false);
  }, [spec.aspectRatio]);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
      setMediaLoaded(false);
      setImageLoadFailed(false);
      setMinZoom(1);
      setZoom(1);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !imageSrc || mediaLoaded || imageLoadFailed) return;
    const timer = setTimeout(() => {
      if (!mediaLoaded) {
        console.warn("[WebsiteUpload] CropDialog: Image failed to load within 8s, enabling fallback");
        setImageLoadFailed(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [open, imageSrc, mediaLoaded, imageLoadFailed]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle>Crop {spec.label} ({spec.width}x{spec.height})</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-[400px] bg-muted rounded-md overflow-hidden">
          {imageSrc && !imageLoadFailed && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              aspect={spec.aspectRatio}
              objectFit="contain"
              restrictPosition={false}
              showGrid={true}
              onMediaLoaded={onMediaLoaded}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, area) => setCroppedAreaPixels(area)}
              style={{
                containerStyle: { background: '#f0f0f0' },
                mediaStyle: { background: '#ffffff' },
              }}
            />
          )}
          {imageLoadFailed && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground text-center">
                Image preview could not load. You can still use the image without cropping.
              </p>
            </div>
          )}
          {!mediaLoaded && !imageLoadFailed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        {mediaLoaded && !imageLoadFailed && (
          <div className="flex items-center gap-4">
            <Label className="shrink-0">Zoom</Label>
            <Slider min={minZoom} max={3} step={0.01} value={[zoom]} onValueChange={([v]) => setZoom(v)} className="flex-1" />
            <span className="text-sm text-muted-foreground w-12 text-right">{zoom.toFixed(2)}x</span>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {(imageLoadFailed || (!croppedAreaPixels && mediaLoaded)) && (
            <Button variant="outline" onClick={handleUseWithoutCrop} disabled={applying} data-testid="button-use-without-crop">
              {applying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Use Without Cropping
            </Button>
          )}
          <Button onClick={handleApply} disabled={applying || !croppedAreaPixels} data-testid="button-apply-crop">
            {applying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Apply Crop
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClickableImage({ src, onFileSelect, className, placeholder, aspect, mediaName, children }: {
  src: string;
  onFileSelect: (file: File) => void;
  className?: string;
  placeholder?: string;
  aspect?: string;
  mediaName?: string;
  children?: React.ReactNode;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      className={`relative group cursor-pointer overflow-hidden ${className || ""} ${dragOver ? "ring-2 ring-primary" : ""}`}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file?.type.startsWith("image/")) onFileSelect(file);
      }}
      data-testid={mediaName ? `upload-zone-${mediaName}` : undefined}
    >
      {src ? (
        <>
          <img src={src} alt={placeholder || "Image"} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center"
               style={{ visibility: "visible" }}>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center text-white">
              <Camera className="h-6 w-6 mb-1" />
              <span className="text-xs font-medium">Change</span>
            </div>
          </div>
        </>
      ) : (
        <div className={`w-full h-full flex flex-col items-center justify-center bg-muted/50 border border-dashed border-muted-foreground/30 text-muted-foreground ${aspect || ""}`}>
          <ImageIcon className="h-6 w-6 mb-1 opacity-50" />
          <span className="text-xs">{placeholder || "Add image"}</span>
        </div>
      )}
      {children}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          console.log("[WebsiteUpload] ClickableImage file input onChange:", { mediaName, hasFile: !!file, fileName: file?.name, fileSize: file?.size });
          if (file) onFileSelect(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

const LOGO_WIDE_VARIANTS: { name: string; width: number; height: number }[] = [
  { name: "site_logo", width: 630, height: 330 },
  { name: "consumer_restaurant_page_logo", width: 350, height: 140 },
  { name: "consumer_restaurant_checkout_logo", width: 256, height: 100 },
  { name: "consumer_restaurant_order_success_logo", width: 256, height: 100 },
  { name: "consumer_restaurant_stripe_logo", width: 256, height: 100 },
];

const LOGO_SQUARE_VARIANTS: { name: string; width: number; height: number }[] = [
  { name: "consumer_restaurant_email_logo", width: 512, height: 512 },
  { name: "consumer_restaurant_list_logo", width: 512, height: 512 },
  { name: "consumer_restaurant_map_logo", width: 256, height: 256 },
  { name: "consumer_restaurant_history_item_logo", width: 96, height: 96 },
  { name: "consumer_restaurant_tracking_details_logo", width: 96, height: 96 },
  { name: "consumer_restaurant_tracking_item_logo", width: 96, height: 96 },
];

const ALL_LOGO_VARIANT_NAMES = [
  ...LOGO_WIDE_VARIANTS.map(v => v.name),
  ...LOGO_SQUARE_VARIANTS.map(v => v.name),
];

const BG_VARIANTS: { name: string; width: number; height: number }[] = [
  { name: "consumer_restaurant_page_background", width: 1024, height: 640 },
  { name: "consumer_restaurant_list_logo", width: 512, height: 320 },
  { name: "consumer_restaurant_map_logo", width: 512, height: 320 },
];

const ALL_BG_VARIANT_NAMES = BG_VARIANTS.map(v => v.name);

async function fitLogoIntoSquare(logo: Blob, size: number): Promise<Blob> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => { URL.revokeObjectURL(el.src); resolve(el); };
    el.onerror = () => reject(new Error("Failed to load image"));
    el.src = URL.createObjectURL(logo);
  });
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);
  const scale = Math.min(size / img.width, size / img.height) * 0.85;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const x = Math.round((size - w) / 2);
  const y = Math.round((size - h) / 2);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, x, y, w, h);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => { if (b) resolve(b); else reject(new Error("Blob creation failed")); }, "image/jpeg", 0.9);
  });
}

function SectionToggle({ label, icon, enabled, onToggle, expanded, onExpandToggle, children, required }: {
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  expanded: boolean;
  onExpandToggle: () => void;
  children: React.ReactNode;
  required?: boolean;
}) {
  const isOn = required || enabled;
  return (
    <div className="border-b border-border/50">
      <div
        className="flex items-center gap-3 py-3 px-1 cursor-pointer select-none"
        onClick={onExpandToggle}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
        <span className="text-muted-foreground shrink-0">{icon}</span>
        <span className="font-medium text-sm flex-1">{label}</span>
        {!required && !enabled && <span className="text-xs text-muted-foreground mr-2">Off</span>}
        {!required && (
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      {expanded && (
        <div className={`pb-6 ${!isOn ? "opacity-40 pointer-events-none" : ""}`}>
          {children}
        </div>
      )}
    </div>
  );
}

function InlineEdit({ value, onChange, placeholder, className, multiline, as }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  as?: "h2" | "h6" | "p" | "span";
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef as any}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
          placeholder={placeholder}
          className={`bg-transparent border border-primary/30 rounded px-2 py-1 w-full resize-none focus:outline-none focus:ring-1 focus:ring-primary/50 ${className || ""}`}
          rows={3}
        />
      );
    }
    return (
      <input
        ref={inputRef as any}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditing(false); }}
        placeholder={placeholder}
        className={`bg-transparent border border-primary/30 rounded px-2 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary/50 ${className || ""}`}
      />
    );
  }

  const displayText = value || placeholder;
  const Tag = as || "span";

  return (
    <Tag
      className={`cursor-text hover:bg-primary/5 rounded px-1 -mx-1 transition-colors inline-block ${!value ? "text-muted-foreground/60 italic" : ""} ${className || ""}`}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Click to edit"
    >
      {displayText}
    </Tag>
  );
}

export default function PartnerWebsite() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem("partnerRestaurantId");
  const eatzyToken = sessionStorage.getItem("partnerEatzyToken");

  const [formData, setFormData] = useState<WebsiteData | null>(null);
  const [pendingImages, setPendingImages] = useState<Map<string, { blob: Blob; previewUrl: string }>>(new Map());
  const [savedPreviews, setSavedPreviews] = useState<Map<string, string>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [cropState, setCropState] = useState<{ src: string; mediaName: string } | null>(null);

  const { data: websiteData, isLoading, error: loadError, refetch } = useQuery<WebsiteData>({
    queryKey: ["/api/partner/website", restaurantId],
    queryFn: async () => {
      const response = await fetch(`/api/partner/website/${restaurantId}`, { headers: getApiHeaders() });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const msg = body?.details || body?.error || `Failed to fetch website data (${response.status})`;
        throw new Error(msg);
      }
      return response.json();
    },
    enabled: !!restaurantId && !!eatzyToken,
    retry: 1,
  });

  useEffect(() => {
    if (websiteData && !formData) {
      const data = JSON.parse(JSON.stringify(websiteData)) as WebsiteData;
      const requiredToggles = ["has_slider", "has_about", "has_contact"];
      for (const name of requiredToggles) {
        const existing = data.text?.find((t: TextEntry) => t.name === name && t.locale === "en");
        if (!existing) {
          data.text = [...(data.text || []), { name, locale: "en", text: "1" }];
        } else if (existing.text !== "1") {
          existing.text = "1";
        }
      }
      const menuUrl = data.live_website ? `${data.live_website.replace(/\/$/, "")}/menu` : "";
      const sliderDefaults: Record<string, string> = {};
      for (const num of ["01", "02", "03"]) {
        sliderDefaults[`slider_${num}_btn`] = "Order Now";
        sliderDefaults[`slider_${num}_btn_link`] = menuUrl;
      }
      for (const [name, defaultVal] of Object.entries(sliderDefaults)) {
        const existing = data.text?.find((t: TextEntry) => t.name === name && t.locale === "en");
        if (!existing) {
          data.text = [...(data.text || []), { name, locale: "en", text: defaultVal }];
        } else if (!existing.text) {
          existing.text = defaultVal;
        }
      }
      setFormData(data);
      const sections = new Set<string>();
      sections.add("hero");
      sections.add("about");
      sections.add("contact");
      const tv = (name: string) => data.text?.find((t: TextEntry) => t.name === name && t.locale === "en")?.text === "1";
      if (tv("has_menu_sample")) sections.add("dishes");
      for (let i = 1; i <= 4; i++) {
        if (tv(`has_custom_${i.toString().padStart(2, "0")}`)) sections.add("custom");
      }
      sections.add("branding");
      sections.add("seo");
      setExpandedSections(sections);
    }
  }, [websiteData, formData]);

  useEffect(() => {
    return () => {
      pendingImages.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
      savedPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const isDirty = useMemo(() => {
    if (!formData || !websiteData) return false;
    if (pendingImages.size > 0) return true;
    return JSON.stringify(formData.text) !== JSON.stringify(websiteData.text) ||
      JSON.stringify(formData.media) !== JSON.stringify(websiteData.media);
  }, [formData, websiteData, pendingImages]);

  const isPublished = formData?.status === "published" || formData?.status === "active";

  const updateText = useCallback((name: string, value: string) => {
    setFormData((prev) => (prev ? { ...prev, text: setTextValue(prev.text || [], name, value) } : prev));
  }, []);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  const handleImageReady = useCallback((mediaName: string, blob: Blob, previewUrl: string) => {
    console.log("[WebsiteUpload] handleImageReady:", mediaName, "blob size:", blob.size);
    setPendingImages(prev => {
      const next = new Map(prev);
      next.set(mediaName, { blob, previewUrl });
      console.log("[WebsiteUpload] pendingImages now has", next.size, "entries:", Array.from(next.keys()));
      return next;
    });
  }, []);

  const handleLogoVariantsReady = useCallback((variants: Map<string, { blob: Blob; previewUrl: string }>) => {
    setPendingImages(prev => {
      const next = new Map(prev);
      variants.forEach((value, key) => {
        const old = next.get(key);
        if (old?.previewUrl) URL.revokeObjectURL(old.previewUrl);
        next.set(key, value);
      });
      return next;
    });
  }, []);

  const processFileForMedia = useCallback(async (file: File, mediaName: string) => {
    console.log("[WebsiteUpload] processFileForMedia called:", { mediaName, fileName: file.name, fileSize: file.size, fileType: file.type });
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }
    let processedFile: Blob = file;
    if (file.size > 2 * 1024 * 1024) {
      console.log("[WebsiteUpload] File exceeds 2MB, resizing...");
      try {
        const resized = await resizeImage(file, { maxWidth: 2048, maxHeight: 2048, quality: 0.85 });
        processedFile = resized.blob.size > 2 * 1024 * 1024
          ? (await resizeImage(resized.blob, { maxWidth: 1600, maxHeight: 1600, quality: 0.75 })).blob
          : resized.blob;
        console.log("[WebsiteUpload] Resized to:", processedFile.size);
      } catch (err) {
        console.error("[WebsiteUpload] Resize failed:", err);
        toast({ title: "Resize failed", description: "Could not resize the image.", variant: "destructive" });
        return;
      }
    }
    const url = URL.createObjectURL(processedFile);
    console.log("[WebsiteUpload] Opening crop dialog for:", mediaName, "url:", url);
    setCropState({ src: url, mediaName });
  }, [toast]);

  const processLogoFile = useCallback(async (file: File) => {
    console.log("[WebsiteUpload] processLogoFile called:", { fileName: file.name, fileSize: file.size, fileType: file.type });
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }
    let processedFile: Blob = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        const resized = await resizeImage(file, { maxWidth: 2048, maxHeight: 2048, quality: 0.85 });
        processedFile = resized.blob.size > 2 * 1024 * 1024
          ? (await resizeImage(resized.blob, { maxWidth: 1600, maxHeight: 1600, quality: 0.75 })).blob
          : resized.blob;
      } catch {
        toast({ title: "Resize failed", variant: "destructive" });
        return;
      }
    }
    const url = URL.createObjectURL(processedFile);
    console.log("[WebsiteUpload] Opening crop dialog for site_logo, url:", url);
    setCropState({ src: url, mediaName: "site_logo" });
  }, [toast]);

  const handleCropComplete = useCallback(async (blob: Blob) => {
    console.log("[WebsiteUpload] handleCropComplete called, blob size:", blob.size, "cropState:", cropState?.mediaName);
    if (!cropState) {
      console.error("[WebsiteUpload] handleCropComplete: cropState is null!");
      return;
    }
    const { mediaName } = cropState;

    try {
      if (mediaName === "site_logo") {
        const previewUrl = URL.createObjectURL(blob);
        handleImageReady("site_logo", blob, previewUrl);
        try {
          const variants = new Map<string, { blob: Blob; previewUrl: string }>();
          for (const variant of LOGO_WIDE_VARIANTS) {
            const resized = await resizeImage(blob, { maxWidth: variant.width, maxHeight: variant.height, quality: 0.9 });
            variants.set(variant.name, { blob: resized.blob, previewUrl: URL.createObjectURL(resized.blob) });
          }
          for (const variant of LOGO_SQUARE_VARIANTS) {
            const fitted = await fitLogoIntoSquare(blob, variant.width);
            variants.set(variant.name, { blob: fitted, previewUrl: URL.createObjectURL(fitted) });
          }
          handleLogoVariantsReady(variants);
          console.log("[WebsiteUpload] Logo variants generated:", variants.size + 1, "total");
          toast({ title: "Logo processed", description: `Generated ${variants.size + 1} sizes ready to save` });
        } catch (err) {
          console.error("[WebsiteUpload] Logo variant generation failed:", err);
          toast({ title: "Logo variant generation failed", variant: "destructive" });
        }
      } else if (mediaName === "consumer_restaurant_page_background") {
        const previewUrl = URL.createObjectURL(blob);
        handleImageReady(mediaName, blob, previewUrl);
        try {
          const variants = new Map<string, { blob: Blob; previewUrl: string }>();
          for (const variant of BG_VARIANTS) {
            const resized = await resizeImage(blob, { maxWidth: variant.width, maxHeight: variant.height, quality: 0.9 });
            variants.set(variant.name, { blob: resized.blob, previewUrl: URL.createObjectURL(resized.blob) });
          }
          handleLogoVariantsReady(variants);
          console.log("[WebsiteUpload] Background variants generated:", variants.size);
          toast({ title: "Background processed", description: `Generated ${variants.size} sizes ready to save` });
        } catch (err) {
          console.error("[WebsiteUpload] Background variant generation failed:", err);
          toast({ title: "Background variant generation failed", variant: "destructive" });
        }
      } else {
        console.log("[WebsiteUpload] Single image ready for:", mediaName);
        const previewUrl = URL.createObjectURL(blob);
        handleImageReady(mediaName, blob, previewUrl);
        toast({ title: "Image ready", description: `Click Save to upload "${mediaName}"` });
      }
    } catch (err: any) {
      console.error("[WebsiteUpload] handleCropComplete top-level error:", err);
      toast({ title: "Image processing failed", description: err.message, variant: "destructive" });
    }
  }, [cropState, handleImageReady, handleLogoVariantsReady, toast]);

  const handleSave = async () => {
    console.log("[WebsiteUpload] handleSave called, pendingImages.size:", pendingImages.size, "keys:", Array.from(pendingImages.keys()));
    if (!formData || !restaurantId) return;
    setIsSaving(true);
    try {
      let updatedMedia = [...(formData.media || [])];
      const headers = getApiHeaders();
      const newImageEntries: Array<{ name: string; file: string; width: number; height: number }> = [];

      const entries = Array.from(pendingImages.entries());
      console.log("[WebsiteUpload] Will upload", entries.length, "images");
      for (let i = 0; i < entries.length; i++) {
        const [mediaName, { blob }] = entries[i];
        console.log("[WebsiteUpload] Uploading", i + 1, "/", entries.length, ":", mediaName, "blob size:", blob.size);
        const slotRes = await fetch("/api/partner/website/upload/slot", { method: "POST", headers });
        if (!slotRes.ok) throw new Error("Failed to create upload slot");
        const slot = await slotRes.json();

        const fd = new FormData();
        fd.append("original_file", blob, "image.jpg");
        fd.append("edited_file", blob, "image.jpg");

        const uploadRes = await fetch(`/api/partner/website/upload/${slot.id}/dual`, {
          method: "POST",
          headers: {
            "X-Eatzy-Token": headers["X-Eatzy-Token"],
            "x-partner-user-id": headers["x-partner-user-id"],
            "x-restaurant-id": headers["x-restaurant-id"],
            Authorization: headers.Authorization,
          },
          body: fd,
        });
        if (!uploadRes.ok) {
          const errBody = await uploadRes.json().catch(() => null);
          throw new Error(errBody?.error || `Failed to upload ${mediaName} (${uploadRes.status})`);
        }
        const uploadData = await uploadRes.json();

        const cdnUrl = uploadData?.edited_url || slot.url || "";
        const file = extractFileFromCdnUrl(cdnUrl);
        const spec = IMAGE_SPECS[mediaName];
        const imgWidth = spec?.width || 512;
        const imgHeight = spec?.height || 512;

        if (mediaName.startsWith("menu_sample_")) {
          const idx = parseInt(mediaName.split("_")[2]);
          newImageEntries.push({ name: "menu_sample", file, width: imgWidth, height: imgHeight });
          updatedMedia = updateMenuSampleMedia(updatedMedia, idx, file, cdnUrl);
        } else {
          newImageEntries.push({ name: mediaName, file, width: imgWidth, height: imgHeight });
          updatedMedia = updateMediaEntry(updatedMedia, mediaName, file, cdnUrl, imgWidth, imgHeight);
        }
      }

      const payload: any = { text: formData.text || [] };
      if (newImageEntries.length > 0) {
        payload.media = newImageEntries;
      }

      const saveRes = await fetch(`/api/partner/website/${restaurantId}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!saveRes.ok) {
        const text = await saveRes.text();
        throw new Error(text || "Failed to save");
      }

      const newSavedPreviews = new Map(savedPreviews);
      pendingImages.forEach(({ previewUrl }, name) => {
        const oldUrl = newSavedPreviews.get(name);
        if (oldUrl) URL.revokeObjectURL(oldUrl);
        newSavedPreviews.set(name, previewUrl);
      });
      setSavedPreviews(newSavedPreviews);
      setPendingImages(new Map());

      toast({ title: "Changes saved", description: "Your website settings have been updated." });
      await queryClient.refetchQueries({ queryKey: ["/api/partner/website", restaurantId] });
      setFormData(null);
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!restaurantId) return;
    setIsPublishing(true);
    try {
      const headers = getApiHeaders();
      const newPublishState = !isPublished;
      const res = await fetch(`/api/partner/website/${restaurantId}/publish`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ publish: newPublishState }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || `Failed to ${newPublishState ? 'publish' : 'unpublish'}`);
      }
      toast({
        title: newPublishState ? "Website published" : "Website unpublished",
        description: newPublishState ? "Your restaurant website is now live." : "Your restaurant website has been taken offline.",
      });
      await queryClient.refetchQueries({ queryKey: ["/api/partner/website", restaurantId] });
      setFormData(null);
    } catch (error: any) {
      toast({ title: "Publish failed", description: error.message, variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!restaurantId || !eatzyToken) {
    return (
      <PartnerLayout>
        <div className="space-y-4 max-w-4xl">
          <h1 className="text-2xl font-bold">Website Builder</h1>
          <Card className="p-6">
            <div className="flex flex-col items-center gap-4 text-center py-8">
              <AlertTriangle className="h-10 w-10 text-muted-foreground" />
              <div className="space-y-1">
                <p className="font-semibold">Session expired</p>
                <p className="text-sm text-muted-foreground">Please log in to the Partner Portal again to manage your website.</p>
              </div>
              <Button variant="outline" onClick={() => window.location.href = "/partner/login"}>
                Go to Login
              </Button>
            </div>
          </Card>
        </div>
      </PartnerLayout>
    );
  }

  if (isLoading) {
    return (
      <PartnerLayout>
        <div className="space-y-6 max-w-4xl">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
          <Skeleton className="h-[600px] w-full" />
        </div>
      </PartnerLayout>
    );
  }

  if (loadError) {
    return (
      <PartnerLayout>
        <div className="space-y-4 max-w-4xl">
          <h1 className="text-2xl font-bold">Website Builder</h1>
          <Card className="p-6">
            <div className="flex flex-col items-center gap-4 text-center py-8">
              <AlertTriangle className="h-10 w-10 text-destructive" />
              <div className="space-y-1">
                <p className="font-semibold">Could not load website data</p>
                <p className="text-sm text-muted-foreground max-w-md">{loadError.message}</p>
              </div>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </Card>
        </div>
      </PartnerLayout>
    );
  }

  if (!formData) {
    return (
      <PartnerLayout>
        <div className="space-y-6 max-w-4xl">
          <h1 className="text-2xl font-bold">Website Builder</h1>
          <p className="text-muted-foreground">Loading website data...</p>
        </div>
      </PartnerLayout>
    );
  }

  const textVal = (name: string) => getTextValue(formData.text || [], name);
  const isToggled = (name: string) => textVal(name) === "1";
  const imgUrl = (name: string) => {
    const preview = pendingImages.get(name)?.previewUrl || savedPreviews.get(name);
    if (preview) return preview;
    const spec = IMAGE_SPECS[name];
    return getMediaUrl(formData.media || [], name, spec?.width || 400, spec?.height || 400);
  };
  const menuImgUrl = (idx: number) => {
    const preview = pendingImages.get(`menu_sample_${idx}`)?.previewUrl || savedPreviews.get(`menu_sample_${idx}`);
    if (preview) return preview;
    const spec = IMAGE_SPECS.menu_sample;
    return getMenuSampleUrl(formData.media || [], idx, spec?.width || 400, spec?.height || 400);
  };
  const hasChanged = (name: string) => pendingImages.has(name);
  const isUploaded = (name: string) => {
    const entry = formData.media?.find((m) => m.name === name);
    return entry?.source === 'uploaded';
  };

  return (
    <PartnerLayout>
      <div className="max-w-4xl" data-testid="website-builder-page">
        {/* Sticky save bar */}
        <div className="sticky top-14 z-40 bg-background/95 backdrop-blur border-b py-3 px-4 -mx-4 mb-4" data-testid="save-bar">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold">Website Builder</h1>
              {isPublished ? (
                <Badge variant="default" className="bg-green-600">Published</Badge>
              ) : (
                <Badge variant="outline">Draft</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isPublished && formData?.live_website && (
                <a href={formData.live_website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline mr-2 hidden sm:inline">
                  View live site
                </a>
              )}
              <Button
                variant={isPublished ? "outline" : "default"}
                size="sm"
                onClick={handlePublishToggle}
                disabled={isPublishing || isSaving}
                data-testid="button-publish"
              >
                {isPublishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Globe className="h-4 w-4 mr-1" />}
                {isPublished ? "Unpublish" : "Publish"}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty} data-testid="button-save">
                {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save{pendingImages.size > 0 ? ` (${pendingImages.size} image${pendingImages.size > 1 ? 's' : ''})` : ''}
              </Button>
            </div>
          </div>
          {isPublished && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              Changes go live immediately when saved
            </p>
          )}
        </div>

        {/* Website preview */}
        <div className="rounded-md border border-border overflow-hidden bg-background">
          {/* == HERO SLIDER == */}
          <SectionToggle
            label="Hero Slider"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="m8 21 4-4 4 4"/></svg>}
            required
            enabled={true}
            onToggle={() => {}}
            expanded={expandedSections.has("hero")}
            onExpandToggle={() => toggleSection("hero")}
          >
            <div className="space-y-4">
              {[
                { num: "01", media: "first_slide" },
                { num: "02", media: "second_slide" },
                { num: "03", media: "third_slide" },
              ].map(({ num, media }, idx) => (
                <div key={num} className="relative">
                  <ClickableImage
                    src={imgUrl(media)}
                    onFileSelect={(f) => processFileForMedia(f, media)}
                    className="w-full aspect-[16/10] rounded-md"
                    placeholder={`Slide ${idx + 1} background`}
                    mediaName={media}
                  >
                    {hasChanged(media) && (
                      <Badge variant="secondary" className="absolute top-2 right-2 z-10">Changed</Badge>
                    )}
                  </ClickableImage>
                  {/* Overlay text editing like the real site */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-8">
                    <div className="bg-black/30 backdrop-blur-sm rounded-md p-4 max-w-md w-full pointer-events-auto space-y-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <InlineEdit
                        value={textVal(`slider_${num}_title`)}
                        onChange={(v) => updateText(`slider_${num}_title`, v)}
                        placeholder="Slide title"
                        className="text-white text-lg font-semibold w-full text-center"
                      />
                      <InlineEdit
                        value={textVal(`slider_${num}_subtitle`)}
                        onChange={(v) => updateText(`slider_${num}_subtitle`, v)}
                        placeholder="Subtitle text"
                        className="text-white/80 text-sm w-full text-center"
                      />
                      <div className="flex items-center justify-center gap-2 pt-1">
                        <InlineEdit
                          value={textVal(`slider_${num}_btn`)}
                          onChange={(v) => updateText(`slider_${num}_btn`, v)}
                          placeholder="Button text"
                          className="text-white text-xs bg-primary/80 rounded px-3 py-1"
                        />
                      </div>
                      <InlineEdit
                        value={textVal(`slider_${num}_btn_link`)}
                        onChange={(v) => updateText(`slider_${num}_btn_link`, v)}
                        placeholder="Button link URL"
                        className="text-white/60 text-xs w-full text-center"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionToggle>

          {/* == ABOUT US == */}
          <SectionToggle
            label="About Us"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>}
            required
            enabled={true}
            onToggle={() => {}}
            expanded={expandedSections.has("about")}
            onExpandToggle={() => toggleSection("about")}
          >
            <div className="flex flex-col md:flex-row gap-4 px-4">
              <ClickableImage
                src={imgUrl("about_bg")}
                onFileSelect={(f) => processFileForMedia(f, "about_bg")}
                className="md:w-1/2 aspect-[4/3] rounded-md shrink-0"
                placeholder="About section image"
                mediaName="about_bg"
              >
                {hasChanged("about_bg") && (
                  <Badge variant="secondary" className="absolute top-2 right-2 z-10">Changed</Badge>
                )}
              </ClickableImage>
              <div className="flex-1 space-y-3 py-2">
                <InlineEdit
                  value={textVal("about_tit")}
                  onChange={(v) => updateText("about_tit", v)}
                  placeholder="About Us"
                  className="text-lg font-semibold block"
                  as="h2"
                />
                <InlineEdit
                  value={textVal("about_desc")}
                  onChange={(v) => updateText("about_desc", v)}
                  placeholder="Tell your story... What makes your restaurant special?"
                  className="text-sm text-muted-foreground leading-relaxed block"
                  multiline
                />
                <div className="border-t border-border/50 pt-3 mt-3 space-y-2">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Call to Action</span>
                  <InlineEdit
                    value={textVal("about_us_cta_tit")}
                    onChange={(v) => updateText("about_us_cta_tit", v)}
                    placeholder="CTA Title"
                    className="text-sm font-medium block"
                  />
                  <InlineEdit
                    value={textVal("about_us_cta_desc")}
                    onChange={(v) => updateText("about_us_cta_desc", v)}
                    placeholder="CTA description"
                    className="text-xs text-muted-foreground block"
                    multiline
                  />
                  <div className="flex items-center gap-2">
                    <InlineEdit
                      value={textVal("about_us_cta_btn")}
                      onChange={(v) => updateText("about_us_cta_btn", v)}
                      placeholder="Button text"
                      className="text-xs bg-primary/10 rounded px-3 py-1"
                    />
                    <InlineEdit
                      value={textVal("about_us_cta_link")}
                      onChange={(v) => updateText("about_us_cta_link", v)}
                      placeholder="Link URL"
                      className="text-xs text-muted-foreground"
                    />
                  </div>
                </div>
              </div>
            </div>
          </SectionToggle>

          {/* == FEATURED DISHES == */}
          <SectionToggle
            label="Featured Dishes"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/></svg>}
            enabled={isToggled("has_menu_sample")}
            onToggle={(v) => { updateText("has_menu_sample", v ? "1" : "0"); setExpandedSections(prev => { const n = new Set(prev); if (v) n.add("dishes"); else n.delete("dishes"); return n; }); }}
            expanded={expandedSections.has("dishes")}
            onExpandToggle={() => toggleSection("dishes")}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4">
              {[0, 1, 2, 3].map((idx) => {
                const num = (idx + 1).toString().padStart(2, "0");
                const pendingKey = `menu_sample_${idx}`;
                return (
                  <div key={idx} className="space-y-2">
                    <ClickableImage
                      src={menuImgUrl(idx)}
                      onFileSelect={(f) => processFileForMedia(f, pendingKey)}
                      className="aspect-square rounded-md"
                      placeholder={`Dish ${idx + 1}`}
                      mediaName={pendingKey}
                    >
                      {hasChanged(pendingKey) && (
                        <Badge variant="secondary" className="absolute top-1 right-1 z-10 text-[10px]">Changed</Badge>
                      )}
                    </ClickableImage>
                    <InlineEdit
                      value={textVal(`menu_sample_${num}_title`)}
                      onChange={(v) => updateText(`menu_sample_${num}_title`, v)}
                      placeholder={`Dish name`}
                      className="text-xs font-medium block truncate"
                    />
                  </div>
                );
              })}
            </div>
          </SectionToggle>

          {/* == CUSTOM SECTIONS == */}
          <SectionToggle
            label="Custom Sections"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>}
            enabled={[1,2,3,4].some(n => isToggled(`has_custom_${n.toString().padStart(2,"0")}`))}
            onToggle={(v) => {
              if (v) {
                updateText("has_custom_01", "1");
                setExpandedSections(prev => { const s = new Set(prev); s.add("custom"); return s; });
              } else {
                [1,2,3,4].forEach(n => updateText(`has_custom_${n.toString().padStart(2,"0")}`, "0"));
                setExpandedSections(prev => { const s = new Set(prev); s.delete("custom"); return s; });
              }
            }}
            expanded={expandedSections.has("custom")}
            onExpandToggle={() => toggleSection("custom")}
          >
            <div className="space-y-4 px-4">
              {[1, 2, 3, 4].map((n) => {
                const num = n.toString().padStart(2, "0");
                const toggleName = `has_custom_${num}`;
                const enabled = isToggled(toggleName);
                const imgName = `custom_image_${num}`;
                const isEven = n % 2 === 0;
                return (
                  <div key={n} className="rounded-md border border-border/50 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30">
                      <span className="text-xs font-medium text-muted-foreground">Section {n}</span>
                      <Switch
                        checked={enabled}
                        onCheckedChange={(checked) => updateText(toggleName, checked ? "1" : "0")}
                        data-testid={`toggle-custom_${num}`}
                      />
                    </div>
                    {enabled && (
                      <div className={`flex flex-col ${isEven ? "md:flex-row-reverse" : "md:flex-row"} gap-3 p-3 ${!enabled ? "opacity-40 pointer-events-none" : ""}`}>
                        <ClickableImage
                          src={imgUrl(imgName)}
                          onFileSelect={(f) => processFileForMedia(f, imgName)}
                          className="md:w-2/5 aspect-[16/10] rounded-md shrink-0"
                          placeholder={`Section ${n} image`}
                          mediaName={imgName}
                        >
                          {hasChanged(imgName) && (
                            <Badge variant="secondary" className="absolute top-1 right-1 z-10 text-[10px]">Changed</Badge>
                          )}
                        </ClickableImage>
                        <div className="flex-1 space-y-2 py-1">
                          <InlineEdit
                            value={textVal(`custom_title_${num}`)}
                            onChange={(v) => updateText(`custom_title_${num}`, v)}
                            placeholder="Section title"
                            className="text-sm font-semibold block"
                          />
                          <InlineEdit
                            value={textVal(`custom_desc_${num}`)}
                            onChange={(v) => updateText(`custom_desc_${num}`, v)}
                            placeholder="Section description..."
                            className="text-xs text-muted-foreground leading-relaxed block"
                            multiline
                          />
                          <div className="flex items-center gap-2">
                            <InlineEdit
                              value={textVal(`custom_btn_${num}`)}
                              onChange={(v) => updateText(`custom_btn_${num}`, v)}
                              placeholder="Button"
                              className="text-xs bg-primary/10 rounded px-3 py-1"
                            />
                            <InlineEdit
                              value={textVal(`custom_link_${num}`)}
                              onChange={(v) => updateText(`custom_link_${num}`, v)}
                              placeholder="Link URL"
                              className="text-xs text-muted-foreground"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionToggle>

          {/* == CONTACT & HOURS == */}
          <SectionToggle
            label="Contact & Hours"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
            required
            enabled={true}
            onToggle={() => {}}
            expanded={expandedSections.has("contact")}
            onExpandToggle={() => toggleSection("contact")}
          >
            <div className="px-4 space-y-3">
              <ClickableImage
                src={imgUrl("contact_hours")}
                onFileSelect={(f) => processFileForMedia(f, "contact_hours")}
                className="w-full aspect-[21/9] rounded-md"
                placeholder="Contact section background"
                mediaName="contact_hours"
              >
                {hasChanged("contact_hours") && (
                  <Badge variant="secondary" className="absolute top-2 right-2 z-10">Changed</Badge>
                )}
              </ClickableImage>
              <p className="text-xs text-muted-foreground">
                Hours, address, and phone are pulled from Restaurant Settings automatically.
              </p>
            </div>
          </SectionToggle>

          {/* == BRANDING == */}
          <SectionToggle
            label="Branding"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l-5.5 9h11z"/><circle cx="12" cy="18" r="4"/></svg>}
            enabled={true}
            onToggle={() => {}}
            expanded={expandedSections.has("branding")}
            onExpandToggle={() => toggleSection("branding")}
          >
            <div className="px-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Logo */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Logo</span>
                    {!hasChanged("site_logo") && isUploaded("site_logo") && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1 text-green-600 border-green-300">Custom</Badge>
                    )}
                  </div>
                  <ClickableImage
                    src={imgUrl("site_logo")}
                    onFileSelect={processLogoFile}
                    className="h-24 rounded-md bg-white border border-border/50"
                    placeholder="Upload logo"
                    mediaName="site_logo"
                  >
                    {hasChanged("site_logo") && (
                      <Badge variant="secondary" className="absolute top-1 right-1 z-10 text-[10px]">Changed</Badge>
                    )}
                  </ClickableImage>
                  {pendingImages.has("site_logo") && (
                    <p className="text-[10px] text-muted-foreground">
                      {ALL_LOGO_VARIANT_NAMES.filter(n => pendingImages.has(n)).length} sizes ready
                    </p>
                  )}
                </div>
                {/* Favicon */}
                <div className="w-32 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Favicon</span>
                    {!hasChanged("favicon") && isUploaded("favicon") && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1 text-green-600 border-green-300">Custom</Badge>
                    )}
                  </div>
                  <ClickableImage
                    src={imgUrl("favicon")}
                    onFileSelect={(f) => processFileForMedia(f, "favicon")}
                    className="h-16 w-16 rounded-md bg-white border border-border/50"
                    placeholder="Favicon"
                    mediaName="favicon"
                  >
                    {hasChanged("favicon") && (
                      <Badge variant="secondary" className="absolute top-0 right-0 z-10 text-[10px] scale-75">New</Badge>
                    )}
                  </ClickableImage>
                </div>
              </div>
              {/* Background */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">App Background / Hero</span>
                  {!hasChanged("consumer_restaurant_page_background") && isUploaded("consumer_restaurant_page_background") && (
                    <Badge variant="outline" className="text-[10px] py-0 px-1 text-green-600 border-green-300">Custom</Badge>
                  )}
                </div>
                <ClickableImage
                  src={imgUrl("consumer_restaurant_page_background")}
                  onFileSelect={(f) => processFileForMedia(f, "consumer_restaurant_page_background")}
                  className="w-full aspect-[16/10] max-h-40 rounded-md"
                  placeholder="App background image"
                  mediaName="consumer_restaurant_page_background"
                >
                  {hasChanged("consumer_restaurant_page_background") && (
                    <Badge variant="secondary" className="absolute top-2 right-2 z-10">Changed</Badge>
                  )}
                </ClickableImage>
                {pendingImages.has("consumer_restaurant_page_background") && (
                  <p className="text-[10px] text-muted-foreground">
                    {ALL_BG_VARIANT_NAMES.filter(n => pendingImages.has(n)).length} sizes ready
                  </p>
                )}
              </div>
            </div>
          </SectionToggle>

          {/* == SEO == */}
          <SectionToggle
            label="SEO"
            icon={<svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>}
            enabled={true}
            onToggle={() => {}}
            expanded={expandedSections.has("seo")}
            onExpandToggle={() => toggleSection("seo")}
          >
            <div className="px-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Page Title</Label>
                <Input
                  value={textVal("title")}
                  onChange={(e) => updateText("title", e.target.value)}
                  placeholder="Restaurant Name - Tagline"
                  className="text-sm"
                  data-testid="input-title"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Meta Description</Label>
                <Textarea
                  value={textVal("description")}
                  onChange={(e) => updateText("description", e.target.value)}
                  placeholder="A brief description for search engines..."
                  className="text-sm min-h-[60px]"
                  data-testid="input-description"
                />
              </div>
            </div>
          </SectionToggle>
        </div>

        {/* Crop dialog */}
        {cropState && (
          <ImageCropDialog
            open={!!cropState}
            onClose={() => {
              if (cropState.src) URL.revokeObjectURL(cropState.src);
              setCropState(null);
            }}
            imageSrc={cropState.src}
            mediaName={cropState.mediaName}
            onCropComplete={handleCropComplete}
          />
        )}
      </div>
    </PartnerLayout>
  );
}
